import { useRouter } from "next/router"
import { useEffect, useState } from "react"

import { FulfillmentType } from "@models/order"
import { useModal } from "@openui"
import { useActiveStore } from "hooks/useActiveStore"
import { useCart } from "providers/cart-provider"

export interface FulfillmentOption {
  id: string
  label: string
  onClick: () => void
  isSelected: boolean
}

interface UseFulfillmentOptionsParams {
  onComplete?: () => void
  loading?: boolean
}

export const useFulfillmentOptions = ({
  onComplete,
  loading,
}: UseFulfillmentOptionsParams) => {
  const { activeStore } = useActiveStore()
  const router = useRouter()
  const [pendingSelection, setPendingSelection] = useState<string | null>(null)
  const { cart, setFulfillmentType } = useCart()
  const curbsideModalController = useModal({
    onCancel: () => setPendingSelection(null),
  })
  const deliveryAddressModalController = useModal({
    onCancel: () => setPendingSelection(null),
  })

  const options: (FulfillmentOption | null)[] = [
    {
      id: "pickup",
      label: "Pickup",
      onClick: () => {
        if (cart.isLoading) return
        if (!cart.data) {
          router.push("/login")
          return
        }
        setPendingSelection("pickup")
        setFulfillmentType.mutate({
          fulfillmentType: FulfillmentType.pickup,
          isCurbside: false,
          curbsidePickupVehicleMakeModel: "",
          curbsidePickupVehicleColor: "",
          curbsidePickupVehicleNotes: "",
        })
      },
      isSelected:
        !loading &&
        (!cart.data ||
          (cart.data?.fulfillmentType === FulfillmentType.pickup &&
            !cart.data?.isCurbside)),
    },
    activeStore?.isCurbsideEnabled
      ? {
          id: "curbside",
          label: "Curbside",
          onClick: () => {
            if (cart.isLoading) return
            if (!cart.data) {
              router.push("/login")
              return
            }
            setPendingSelection("curbside")
            curbsideModalController.open()
          },
          isSelected:
            !loading &&
            cart.data?.fulfillmentType === FulfillmentType.pickup &&
            cart.data.isCurbside,
        }
      : null,
    {
      id: "delivery",
      label: "Delivery",
      onClick: () => {
        if (cart.isLoading) return
        if (!cart.data) {
          router.push("/login")
          return
        }
        setPendingSelection("delivery")
        deliveryAddressModalController.open()
      },
      isSelected:
        !loading && cart.data?.fulfillmentType === FulfillmentType.delivery,
    },
  ]

  useEffect(() => {
    if (
      cart.data?.fulfillmentType === FulfillmentType.pickup &&
      !cart.data?.isCurbside &&
      pendingSelection === "pickup"
    ) {
      setPendingSelection(null)
      onComplete?.()
    } else if (
      cart.data?.fulfillmentType === FulfillmentType.pickup &&
      cart.data?.isCurbside &&
      pendingSelection === "curbside"
    ) {
      setPendingSelection(null)
      onComplete?.()
    } else if (
      cart.data?.fulfillmentType === FulfillmentType.delivery &&
      pendingSelection === "delivery"
    ) {
      setPendingSelection(null)
      onComplete?.()
    }
  }, [
    cart.data?.fulfillmentType,
    cart.data?.isCurbside,
    pendingSelection,
    onComplete,
  ])

  return {
    options,
    pendingSelection,
    curbsideModalController,
    deliveryAddressModalController,
    setPendingSelection,
  }
}
